// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
// oxlint-disable-next-line no-restricted-imports
import { type NavigateOptions, useNavigate } from 'react-router';

import { moduleRoute } from 'app/Layout/NavBar/globalNavigationMenu';
import type { ShopNavigationRoute } from 'app/ModuleRoutes';
import { useAppNeedsRefresh } from 'components/Mantine/Navigation/useAppNeedsRefresh';

import type { LocationState } from './types';
import { useEvolveLocation } from './useEvolveLocation';

type ValidTo = ShopNavigationRoute | number;

export const calculateNewState = ({
  state,
  clearHistory,
  to,
  from,
  additionalData,
}: {
  to: ValidTo;
  state: LocationState;
  additionalData?: unknown;
} & (
  | {
      from: string;
      clearHistory?: boolean;
    }
  | {
      from?: string;
      clearHistory: true;
    }
)): LocationState => {
  if (clearHistory) {
    return {
      ...state,
      additionalData,
      from: undefined,
    };
  }
  if (state?.from?.[0]?.url === window.location.pathname || `/${moduleRoute}${to}` === window.location.pathname) {
    return { ...state, additionalData };
  }
  return {
    ...state,
    additionalData,
    from: [
      {
        title: from,
        url: window.location.pathname as ShopNavigationRoute,
        index: state?.from?.length ?? 0,
      },
      ...(state?.from ?? []),
    ],
  };
};

export const getHistory = (locationState: LocationState) => {
  if (!locationState?.from) return [];
  if (Array.isArray(locationState.from)) {
    return locationState.from.toSorted((a, b) => a.index - b.index);
  }
  const locationString: string = locationState.from;
  return [
    {
      title: locationString,
      url: `shop/${locationString.toLowerCase().replace(' ', '-')}`,
      index: 0,
    },
  ];
};

/**
 * A hook that returns a function that can be used to navigate to a new page.
 * @param pageTitle the title of the page we are navigating from. This will be used to generate breadcrumbs.
 * @param clearHistory when true, the history state used to track breadcrumbs will be cleared.
 * @param navigateBack when true, if the page we are navigating to is already in the history, will navigate back to it instead of pushing a new entry onto the history.
 */
export const useEvolveNavigate = (pageTitle: string, clearHistory = false, navigateBack = false) => {
  const { updateAvailable, hardRefresh } = useAppNeedsRefresh();
  const { state } = useEvolveLocation();
  const navigate = useNavigate();
  return useCallback(
    async (
      to: ValidTo,
      {
        additionalData,
        ...options
      }: StrongOmit<
        NavigateOptions,
        // state is managed internally
        'state'
      > & {
        additionalData?: unknown;
      } = {},
    ) => {
      if (typeof to === 'number') {
        await navigate(to);
      } else {
        const historyIndexOfToLoc = navigateBack
          ? getHistory(state).find((h) => h.url === `/${moduleRoute}${to}`)?.index
          : undefined;
        if (historyIndexOfToLoc !== undefined) {
          await navigate(historyIndexOfToLoc - history.length);
        } else {
          await navigate(`/${moduleRoute}${to}`, {
            ...options,
            state: calculateNewState({
              to,
              state,
              from: pageTitle,
              clearHistory,
              additionalData,
            }),
          });
        }
      }
      if (updateAvailable) {
        hardRefresh();
      }
    },
    [clearHistory, hardRefresh, navigate, pageTitle, state, updateAvailable, navigateBack],
  );
};
